import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@scio3/ui';
import {
  GET_UPCOMING_AND_UNPAID_SUBSCRIPTION,
  PAYMENT_INVOICES_UNPAID,
} from '@scio3/graphql-client';
import { toast } from 'sonner';

type UnpaidInvoicesAlertProps = {
  subAccountId?: string;
  teamId?: string;
};

/**
 * Warning banner shown when the account has unpaid invoices.
 * Includes a "Pay Now" button that calls paymentInvoicesUnpaid mutation.
 * Reusable in PaymentMethodPage and SubscriptionPage.
 */
export function UnpaidInvoicesAlert({ subAccountId, teamId }: UnpaidInvoicesAlertProps) {
  const { t } = useTranslation();
  const { data, loading: queryLoading } = useQuery(GET_UPCOMING_AND_UNPAID_SUBSCRIPTION, {
    variables: { subAccountId, teamId },
    fetchPolicy: 'network-only',
  });

  const hasUnpaid =
    (data as Record<string, { status?: boolean } | undefined> | undefined)
      ?.getUpcomingAndUnpaidSubscription?.status === false;

  const [payUnpaid, { loading }] = useMutation(PAYMENT_INVOICES_UNPAID);

  const handlePayNow = async () => {
    try {
      const result = await payUnpaid({ variables: { subAccountId, teamId } });
      const response = (
        result.data as
          | Record<string, { status?: boolean; message?: string } | undefined>
          | undefined
      )?.paymentInvoicesUnpaid;
      if (response?.status) {
        toast(t('Payment processed successfully'));
      } else {
        toast.error(response?.message ?? t('Payment failed'));
      }
    } catch {
      toast.error(t('Failed to process payment'));
    }
  };

  // Wait for API to finish before deciding to show — prevents flash
  if (queryLoading || !hasUnpaid) return null;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-warning bg-warning/10 p-4">
      <AlertTriangle className="h-5 w-5 shrink-0 text-warning" />
      <div className="flex-1 text-sm">
        <p className="font-medium">{t('You have unpaid invoices')}</p>
        <p className="text-muted-foreground">
          {t('Please update your payment method or pay outstanding invoices.')}
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handlePayNow}
        disabled={loading}
        className="cursor-pointer shrink-0"
      >
        {loading ? t('Processing...') : t('Pay Now')}
      </Button>
    </div>
  );
}
